import { dinero, toDecimal, greaterThan, subtract } from "dinero.js";
import { GBP } from "dinero.js/currencies";
import type { Dinero } from "dinero.js";

export type MoneyInput = Dinero<number> | number;

export function dineroFromFloat(amount: number): Dinero<number> {
  return dinero({ amount: Math.round(amount * 10 ** GBP.exponent), currency: GBP });
}

export function coerceMoney(input: MoneyInput): Dinero<number> {
  return typeof input === "number" ? dineroFromFloat(input) : input;
}

// Every Dinero<number> in this codebase is GBP (dineroFromFloat hardcodes it), so a
// single lazily-constructed formatter is sufficient — a Map keyed by locale/currency
// would only ever hold one entry. Revisit if DUSK ever expands beyond single-currency/
// single-locale.
let formatter: Intl.NumberFormat | undefined;

function getFormatter(): Intl.NumberFormat {
  return (formatter ??= new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }));
}

export function formatMoney(d: Dinero<number>): string {
  return toDecimal(d, ({ value }) => getFormatter().format(Number(value)).replace(/\.00$/, ""));
}

// Shared by lib/badges/rules.ts (promo overlay badge) so the "Save" wording/amount
// can't drift between surfaces.
export function formatSaveBadgeText(
  price: MoneyInput,
  compareAtPrice: MoneyInput | undefined,
  prefix = "",
): string | null {
  const normalizedPrice = coerceMoney(price);
  const normalizedCompareAtPrice = compareAtPrice != null ? coerceMoney(compareAtPrice) : undefined;
  if (!normalizedCompareAtPrice || !greaterThan(normalizedCompareAtPrice, normalizedPrice)) {
    return null;
  }
  const saving = subtract(normalizedCompareAtPrice, normalizedPrice);
  return `Save ${prefix ? `${prefix} ` : ""}${formatMoney(saving)}`;
}
